import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://regard-de-nature.fr";

const NaturopatheFatigue = () => {
  useSEO({
    title: "Fatigue chronique : solution naturelle – Regard de Nature",
    description:
      "Retrouver son énergie naturellement grâce à la naturopathie : alimentation, sommeil, hygiène de vie, plantes. Accompagnement personnalisé par Laura Elie.",
    canonical: `${SITE}/naturopathe-fatigue`,
  });

  return (
    <SeoPageLayout
      h1="Retrouver son énergie naturellement grâce à la naturopathie"
      intro="Réveil difficile, café pour démarrer, coup de barre vers 15 h, mental embrumé, week-ends qui ne suffisent plus à récupérer ? La fatigue qui s'installe dans la durée mérite qu'on s'y attarde — et qu'on agisse, pas à pas."
    >
      <SeoSection title="Une fatigue qui ne lâche pas">
        <p>
          Je reçois beaucoup de personnes qui me disent la même chose : « j'ai l'impression de tirer sur la
          corde ». Vous vous réveillez fatigué·e, vous traînez en journée, vous avez besoin de cafés ou de sucre
          pour tenir, vos émotions sont à fleur de peau. La fatigue chronique est l'un des motifs de consultation
          les plus fréquents, et pourtant elle reste souvent banalisée.
        </p>
        <p>
          La naturopathie ne pose pas de diagnostic médical et ne soigne pas la fatigue en tant que maladie. En
          revanche, elle propose un accompagnement global pour identifier ce qui pèse sur votre vitalité et
          reconstruire vos ressources naturelles.
        </p>
      </SeoSection>

      <SeoSection title="Ce que vous pouvez retrouver">
        <SeoList
          items={[
            "Une énergie plus stable du matin au soir, sans béquilles permanentes.",
            "Un réveil plus facile, sans cette sensation de « ne pas avoir dormi ».",
            "Une meilleure récupération après les efforts ou les semaines chargées.",
            "Un mental plus clair, moins embrumé en fin de journée.",
            "Le sentiment de redevenir acteur·rice de votre vitalité.",
          ]}
        />
      </SeoSection>

      <SeoSection title="Comprendre les causes de votre fatigue">
        <p>
          La fatigue est rarement due à une seule cause. J'observe presque toujours un cumul : sommeil de mauvaise
          qualité, alimentation déséquilibrée, stress chronique, charge mentale, manque d'activité physique,
          exposition aux écrans, environnement de vie, événements émotionnels.
        </p>
        <p>
          Dans ma pratique, je prends le temps avec vous d'identifier ce qui vous épuise et ce qui peut être
          ajusté pas à pas, dans la durée — sans tout changer en même temps.
        </p>
      </SeoSection>

      <SeoSection title="Les axes de l'accompagnement">
        <SeoSubSection title="Restaurer le sommeil">
          <p>
            Sans sommeil de qualité, pas de récupération. Nous travaillons sur les rythmes, le rituel du soir, les
            apports en micronutriments. Voir aussi :{" "}
            <Link to="/naturopathe-sommeil" className="text-primary underline hover:text-secondary">naturopathie & sommeil</Link>.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Soutenir l'alimentation">
          <SeoList
            items={[
              "Stabilité glycémique pour éviter les coups de barre",
              "Apports en fer, magnésium, vitamines B",
              "Repas adaptés à votre rythme",
              "Soutien du foie et de la digestion",
            ]}
          />
        </SeoSubSection>
        <SeoSubSection title="Apaiser le stress">
          <p>
            Le{" "}
            <Link to="/naturopathe-stress" className="text-primary underline hover:text-secondary">stress chronique</Link>{" "}
            épuise littéralement les surrénales et la vitalité. Travailler sur la respiration, la nature, les
            limites, les temps de repos est essentiel.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Soutenir avec les plantes">
          <p>
            Plantes adaptogènes, élixirs floraux, hydrolats : autant de ressources naturelles que je peux vous
            suggérer selon votre profil, à titre éducatif.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="L'iridologie, un éclairage complémentaire">
        <p>
          L'<Link to="/iridologie" className="text-primary underline hover:text-secondary">iridologie</Link> peut, lors
          du bilan, m'aider à mieux cerner votre terrain et vos sensibilités. Ce n'est pas un diagnostic, mais un
          outil d'observation parmi d'autres.
        </p>
      </SeoSection>

      <SeoSection title="Ce qui guide ma pratique">
        <p>
          Reconstruire son énergie demande du temps et de la patience. Mon approche est progressive : je ne propose
          pas de cure miracle, mais une démarche réaliste, alignée avec votre quotidien. J'observe que les
          personnes qui retrouvent durablement de l'énergie sont celles qui ont accepté d'avancer pas à pas.
        </p>
        <p>
          Je consulte à{" "}
          <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
            Montesquieu-Lauragais
          </Link>{" "}
          et à{" "}
          <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
            Villefranche-de-Lauragais
          </Link>
          .
        </p>
        <p className="italic text-foreground/70">
          Si votre fatigue est très importante, persistante ou s'accompagne d'autres symptômes (perte de poids,
          fièvre, douleurs, etc.), consultez votre médecin traitant pour écarter toute cause médicale.
        </p>
      </SeoSection>

      {/* CTA fin de page */}
      <section className="mt-10 sm:mt-12 p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border border-border rounded-lg text-center">
        <h2 className="text-xl sm:text-2xl font-cinzel font-bold text-primary mb-3">
          Retrouver de l'énergie, à votre rythme
        </h2>
        <div className="w-12 h-px bg-secondary mx-auto mb-4" />
        <p className="text-base text-foreground/80 font-montserrat mb-5 max-w-2xl mx-auto">
          Prendre rendez-vous, c'est s'autoriser un temps pour comprendre d'où vient cette fatigue — et poser les
          premiers leviers concrets pour la dénouer.
        </p>
        <Link to="/#accompagnements">
          <Button size="lg" className="bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
            <Calendar className="w-5 h-5 mr-2" />
            Faire le point sur mon énergie
          </Button>
        </Link>
      </section>
    </SeoPageLayout>
  );
};

export default NaturopatheFatigue;
